/**
 * SG deck mirror seed — shared between web and RN folder/listing views.
 *
 * Under Option F each linked SG deck has exactly one per-user local
 * mirror that carries both shared fields (kept fresh by UserDo's
 * cross-DO fan-out) and personal fields (studyState, actions, starred,
 * tags, notes, chat) written by study sessions. The mirror is what
 * `DeckCard` / `DeckViewLoaded` / quiz / memo render — there's no
 * parallel SG view in personal-mode code paths.
 *
 * `seedOrMergeSGMirror` mints a mirror the first time a member sees an
 * SG deck, AND — when a mirror already exists — non-destructively merges
 * the SG's shared cards into it (LWW, preserving personal-scope fields).
 * The merge branch is why a mirror that was first seeded before its
 * cards had synced (the "member sees 0 cards" bug) self-heals: reactive
 * callers re-invoke once the cards arrive. It never wipes local-only
 * cards or the caller's personal state. Subsequent shared-field updates
 * also arrive via the UserDo cross-DO fan-out handler
 * (`/internal/apply-shared-field-from-group`); personal-field writes
 * come from the scoped deck facade.
 *
 * `seedMirrorIfMissing` is kept as a deprecated alias (see bottom).
 *
 * Two callers, two jobs. A SURFACE that renders the deck (mobile group deck
 * list, web SG deck route) wants the full seed-or-merge. The ROOT orchestrator
 * (`StudyGroupSyncOrchestrator`) only needs the IDENTITY half — mint a missing
 * mirror, repoint a stale link after a delete→re-share — so it passes
 * `mergeCards: false`. Merging at root re-ran `mergeCardsLWW` + a store write +
 * one SQLite upsert per card for EVERY deck in EVERY group on app start and on
 * every group card frame (~20k encodes per pass for a member of two large
 * groups), which is what pinned the Home screen at ~0 fps on Android.
 *
 * The seed deliberately pushes to UserDo without `withServerSource`:
 * the server's `propagateSharedFieldsToLinkedGroups` reads
 * `meta.linkedGroups` off the deckmeta row to fan card-pushes out to
 * each linked StudyGroupDo, and `card-push` itself is dropped server-
 * side when the deckmeta row is missing. Suppressing the seed push
 * therefore makes the mirror server-invisible — every later edit
 * targets a UUID the server has no record of, so cards never propagate
 * and never reach other devices for the same user.
 */
import {
  cardTimestamp,
  mergeCardsLWW,
} from '@flashka/shared/sync-engine/framework/sync-merge';
import { PERSONAL_CARD_FIELDS } from '@flashka/shared/sync-engine/schema/field-scopes';
import type { SGDeckEditPermission } from '@flashka/shared/sync-engine/schema/group-entities';
import type {
  DeckId,
  SyncCardProps,
  SyncLinkedGroup,
} from '@flashka/shared/sync-engine/schema/shared-types';
import { asDeckId, asDeckPath } from '@flashka/shared/sync-engine/schema/shared-types';

import { deckCardsMergeSafe } from '../internal/deck-residency';
import { withServerSource } from '../internal/framework/mutation-source';
import { perfMark } from '../internal/perf-log'; // [perf-probe]
import { isPersonalSyncReady } from '../internal/personal-sync-ready';
import { findLocalDeckIdForSGDeck } from '../internal/sg-mirror-resolver';
import { useFlashkaStore } from '../internal/store/flashka-store';
import { useStudyGroupStore } from '../internal/store/study-group-store';
import { uuid } from '../internal/uuid';
import type { DeckProperties } from '../provider/sync-storage';
import { sync } from '../sync';

/** Build the `__sg_{groupId}_{deckName}` mirror deckPath. Single source of truth. */
export function sgKey(groupId: string, deckName: string): string {
  return `__sg_${groupId}_${deckName}`;
}

export interface SeedMirrorArgs {
  groupId: string;
  sgDeckId: string;
  deckName: string;
  cards: SyncCardProps[];
  viewerUserId: string | null;
  ownerUserId: string;
  displayName?: string;
  color?: string;
  editPermission: SGDeckEditPermission;
  /**
   * `false` = identity-only: mint a missing mirror (with `cards`), repoint or
   * re-link a stale one, but never merge `cards` into an EXISTING mirror. Used
   * by the root orchestrator, which must not pay a per-card merge for every
   * group deck on every frame. Default `true` (seed-or-merge).
   */
  mergeCards?: boolean;
}

/**
 * Would merging this incoming SG card change the mirror? Mirrors
 * `mergeCardsLWW`'s decision for one card so a re-seed with cards the mirror
 * already holds is a no-op instead of a rewrite: a newer incoming timestamp
 * wins; an older one loses; on a TIE only the tombstone-biased flip counts (an
 * equal timestamp is the same shared version — the overlay of local personal
 * fields makes the objects differ by identity, not by content).
 */
function sgCardChangesMirror(
  local: SyncCardProps | undefined,
  incoming: SyncCardProps,
): boolean {
  if (!local) return true;
  const tsIn = cardTimestamp(incoming);
  const tsLocal = cardTimestamp(local);
  if (tsIn > tsLocal) return true;
  if (tsIn < tsLocal) return false;
  return !!incoming.deletedTimeISO !== !!local.deletedTimeISO;
}

function mirrorOfLiveNamesake(
  props: DeckProperties | undefined,
  args: SeedMirrorArgs,
): boolean {
  const linked = props?.linkedGroups?.find((l) => l.groupId === args.groupId)?.sgDeckId;
  if (!linked || linked === args.sgDeckId) return false;
  const deck = useStudyGroupStore.getState().groups[args.groupId]?.deckProps?.[linked];
  // Unknown to the store (not synced yet, or gone) is not evidence of a live
  // namesake: keep the re-share heal.
  return !!deck && !deck.deletedTimeISO;
}

/**
 * Seed the caller's personal `sync.decks` store with a mirror of a
 * shared deck. When no mirror exists yet it mints one; when a mirror
 * already exists it leaves identity/props alone and merges in the SG's
 * shared cards (LWW, preserving personal fields). Returns the local
 * `DeckId` of the mirror (existing or newly minted), or `null` while a mint
 * has to wait for the personal store to catch up with the server.
 *
 * Idempotent and safe to call reactively on every shared-cards change —
 * an empty first seed (cards not yet synced) is healed by a later call
 * once `args.cards` is populated.
 */
export function seedOrMergeSGMirror(args: SeedMirrorArgs): DeckId | null {
  const sgKeyStr = sgKey(args.groupId, args.deckName);
  const allProps = (sync.decks.getProperties() ?? {}) as Record<string, DeckProperties>;
  const found = findLocalDeckIdForSGDeck(allProps, args.groupId, args.sgDeckId, sgKeyStr);
  // The `__sg_{group}_{deckName}` fallback also matches the mirror of ANOTHER
  // deck with the same name. When that deck is still live in the group the
  // mirror is its, not stale: this deck gets its own. Repointing it instead
  // made the two decks take turns — every seed rewrote the mirror, the write
  // re-ran the seeding effect, and React gave up ("Maximum update depth
  // exceeded", FLASHKA-VITE-2GV) after merging each deck's cards into the
  // other's mirror.
  const existingId = found && !mirrorOfLiveNamesake(allProps[found], args) ? found : null;
  if (existingId) {
    // Fix A — reconcile stale linkedGroups before short-circuiting.
    // If the resolver matched via the deckPaths fallback (i.e. there's
    // no linkedGroups entry for this group that matches the current
    // sgDeckId), the existing mirror is pointing at a *previous*
    // sgDeckId — typically because the owner removed the deck from
    // the group and re-shared, minting a fresh sgDeckId server-side.
    // Members never receive a "drop your link" broadcast today, so
    // their mirror stays pointed at the tombstoned sgDeckId forever:
    // every subsequent cross-DO fan-out (`apply-shared-field-from-group`)
    // returns `unknown-deck` and the member's UserDo never learns about
    // any new card.
    //
    // Scoping rules — see also sg-deck-view.tsx:saveToDexie:
    //   - Rewrite only the `linkedGroups[]` entry for THIS groupId;
    //     leave other groups' entries alone.
    //   - Only rewrite when an entry already exists for this group AND
    //     its sgDeckId is wrong. If there's NO entry for this group
    //     (deliberately unlinked, or never linked), do NOT add one —
    //     orphans must stay dead.
    const existing = allProps[existingId] as DeckProperties | undefined;
    const link = existing?.linkedGroups?.find((l) => l.groupId === args.groupId);
    if (existing && link && link.sgDeckId !== args.sgDeckId) {
      // Fix A — STALE link → repoint to the freshly-minted sgDeckId.
      const repointed = (existing.linkedGroups ?? []).map((l) =>
        l.groupId === args.groupId
          ? {
              ...l,
              sgDeckId: args.sgDeckId,
              linkedAtISO: new Date().toISOString(),
              editPermission: args.editPermission,
            }
          : l,
      );
      sync.decks.setProperties(asDeckId(existingId), {
        ...existing,
        linkedGroups: repointed,
        // Refresh `originDeckId` for member-side mirrors so the
        // member's `originDeckId` keeps pointing at the deck row the
        // server now considers canonical. Owners' originDeckId stays
        // undefined per the constructor below.
        originDeckId:
          existing.originUserId && existing.originUserId !== args.viewerUserId
            ? asDeckId(args.sgDeckId)
            : existing.originDeckId,
      });
    } else if (existing && !link && existing.deckPaths?.includes(asDeckPath(sgKeyStr))) {
      // Fix C — RE-LINK an orphaned `__sg_` mirror on re-share.
      //
      // When the deck owner removes a deck from the group, the server's
      // `/internal/unlink-deck` fan-out STRIPS this group's `linkedGroups`
      // entry from the member's mirror but keeps the row and its stable
      // `__sg_{groupId}_{deckName}` deckPath. On re-share the deck gets a
      // FRESH sgDeckId, the linkedGroups pass misses, and we arrive here via
      // the deckPaths fallback with no entry to repoint. Leaving it empty
      // (the old "orphans stay dead" rule) permanently breaks the mirror: no
      // `linkedGroups` ⇒ the source overview can't compose (no source tab)
      // and the cross-DO card fan-out returns `unknown-deck` (no live sync).
      // Re-add a fresh link instead.
      //
      // Safe because this only fires when a LIVE SG deck is being seeded (the
      // seeders skip deleted decks) whose `__sg_` deckPath matches THIS group
      // + deckName — i.e. a genuine re-share. A deck that was deleted and NOT
      // re-added is never seeded, so a truly-orphaned row still stays dead.
      const viewerRole: SyncLinkedGroup['role'] =
        args.viewerUserId && args.ownerUserId === args.viewerUserId ? 'owner' : 'member';
      const relinked: SyncLinkedGroup[] = [
        ...(existing.linkedGroups ?? []),
        {
          groupId: args.groupId,
          sgDeckId: args.sgDeckId,
          linkedAtISO: new Date().toISOString(),
          editPermission: args.editPermission,
          role: viewerRole,
        },
      ];
      sync.decks.setProperties(asDeckId(existingId), {
        ...existing,
        linkedGroups: relinked,
        originUserId: viewerRole === 'member' ? args.ownerUserId : existing.originUserId,
        originDeckId:
          viewerRole === 'member' ? asDeckId(args.sgDeckId) : existing.originDeckId,
      });
    }

    // Non-destructive card merge — copy the SG's shared cards into the
    // mirror without clobbering local-only cards or the member's
    // personal-scope fields (starred / studyState / notes / chat / …).
    // Skip when the SG cards haven't synced yet: merging an empty
    // snapshot would be a no-op and risks racing real data, and this is
    // exactly the "seeded before cards arrived → 0 cards" window — a
    // reactive caller re-invokes once `args.cards` lands and heals here.
    //
    // Three reasons NOT to merge, each deliberate:
    //   - `mergeCards: false` — the root orchestrator's identity-only pass.
    //   - the mirror's bodies are withheld (cold) — merging would overlay
    //     personal fields from an EMPTY bucket and erase the member's study
    //     history on this device (see `deckCardsMergeSafe`).
    //   - nothing changed — every incoming card is already in the mirror at
    //     the same or a newer version. This is the common case on a re-seed
    //     and used to cost a full `setDeck` + per-card SQLite upsert anyway.
    const state = useFlashkaStore.getState();
    if (args.mergeCards === false || args.cards.length === 0) {
      // identity-only, or cards not synced yet — nothing to merge.
    } else if (!deckCardsMergeSafe(state, existingId)) {
      perfMark('sg:mirror-seed skip (bodies withheld)', () => ({
        group: args.groupId.slice(0, 8),
        sgDeck: args.sgDeckId.slice(0, 8),
      })); // [perf-probe]
    } else if (
      !args.cards.some((card) =>
        sgCardChangesMirror(
          state.cards[existingId]?.[card.id] as SyncCardProps | undefined,
          card,
        ),
      )
    ) {
      perfMark('sg:mirror-seed skip (no change)', () => ({
        group: args.groupId.slice(0, 8),
        sgDeck: args.sgDeckId.slice(0, 8),
        cards: args.cards.length,
      })); // [perf-probe]
    } else {
      const localCardMap = state.cards[existingId] ?? {};
      const localCardsArr = Object.values(localCardMap) as SyncCardProps[];
      // Overlay the member's personal-scope fields onto the incoming SG
      // cards so the LWW merge can't drop them when the shared half wins.
      const sgWithLocalPersonal = args.cards.map((card) => {
        const local = localCardMap[card.id] as Record<string, unknown> | undefined;
        if (!local) return card;
        const out = { ...card } as Record<string, unknown>;
        for (const key of PERSONAL_CARD_FIELDS) {
          if (local[key as string] !== undefined)
            out[key as string] = local[key as string];
        }
        return out as SyncCardProps;
      });
      const merged = mergeCardsLWW(localCardsArr, sgWithLocalPersonal);
      perfMark('sg:mirror-seed merge', () => ({
        group: args.groupId.slice(0, 8),
        sgDeck: args.sgDeckId.slice(0, 8),
        cards: merged.length,
      })); // [perf-probe]
      // `withServerSource`: these cards are already canonical on the
      // StudyGroupDo, so don't re-propagate them back out on write.
      withServerSource(() => {
        sync.decks.setDeck(asDeckId(existingId), merged);
      });
      // `merged` ⊇ every local row (merge-safe) ∪ the group's full shared set,
      // and after `setDeck` storage holds every row of it: it writes every row
      // unless the bucket was already loaded / disk-loaded, and then skips only
      // cards whose reference is unchanged — already byte-identical rows. The
      // mirror is resident now and memory covers disk, whatever partial set
      // disk held before.
      useFlashkaStore.getState().markDeckCardsLoaded(existingId, { coversDisk: true });
    }

    return asDeckId(existingId);
  }

  // No mirror here may only mean this device has not heard of it yet: one
  // minted on another device arrives in this session's UserDo diff, which the
  // (smaller) group socket often beats. Minting now made a TWIN, and the
  // member's history stayed on the real mirror, out of sight. The callers
  // re-run when the store is reconciled (`usePersonalSyncReady`).
  if (!isPersonalSyncReady()) return null;

  const localDeckId = asDeckId(uuid());
  const viewerRole: SyncLinkedGroup['role'] =
    args.viewerUserId && args.ownerUserId === args.viewerUserId ? 'owner' : 'member';
  const link: SyncLinkedGroup = {
    groupId: args.groupId,
    sgDeckId: args.sgDeckId,
    linkedAtISO: new Date().toISOString(),
    editPermission: args.editPermission,
    role: viewerRole,
  };
  sync.decks.setProperties(localDeckId, {
    id: localDeckId,
    deckPaths: [asDeckPath(sgKeyStr)],
    displayName: args.displayName || args.deckName,
    color: args.color,
    addedTimeISO: new Date().toISOString(),
    linkedGroups: [link],
    originUserId: viewerRole === 'member' ? args.ownerUserId : undefined,
    originDeckId: viewerRole === 'member' ? asDeckId(args.sgDeckId) : undefined,
    // The count a later session needs to tell a full disk read from a short
    // one (`deckCardsMergeSafe`); unknown, not 0, when minted without cards.
    ...(args.cards.length > 0
      ? { cardCount: args.cards.filter((c) => !c.deletedTimeISO).length }
      : {}),
  });
  perfMark('sg:mirror-seed mint', () => ({
    group: args.groupId.slice(0, 8),
    sgDeck: args.sgDeckId.slice(0, 8),
    cards: args.cards.length,
  })); // [perf-probe]
  sync.decks.setDeck(localDeckId, args.cards);
  return localDeckId;
}

/**
 * @deprecated Use {@link seedOrMergeSGMirror}. Kept as a thin alias so
 * existing call sites keep compiling during the migration. Behaviour is
 * now seed-OR-merge: it also backfills/merges cards into an existing
 * mirror (the old name only ever seeded a missing one).
 */
export function seedMirrorIfMissing(args: SeedMirrorArgs): DeckId | null {
  return seedOrMergeSGMirror(args);
}
